import {
  SURFACE_PAGE_TAB_ACTIVE,
  SURFACE_PAGE_TAB_INACTIVE,
} from '@/shared/ui-class';
import { cn } from '@/shared/cn';

export type SurfacePageTabItem<T extends string = string> = {
  id: T;
  label: string;
  /** Optional headline figure set beside the label (`314.0 h`, `2 of 5`), so a
   *  tab carries its number before it is opened. */
  count?: string;
};

export type SurfacePageTabsProps<T extends string> = {
  items: readonly SurfacePageTabItem<T>[];
  value: T;
  onChange: (next: T) => void;
  'aria-label'?: string;
  className?: string;
  /** `soft` (default) = filled neutral pill; `ink` = the Advisory-standard
   *  drill-down row, active tab filled in ink like a primary action. */
  variant?: 'soft' | 'ink';
  /** `ink` only, opt-in: `compact` sets the pills at the comp's 12 px on a
   *  tighter pad and gap, for a tab row that shares a narrow column (the
   *  Advisory role card's 5/12 tab column). Default keeps the standard row. */
  density?: 'default' | 'compact';
};

const INK_TAB_BASE = 'inline-flex items-baseline gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors duration-150';
const INK_TAB_COMPACT = 'inline-flex items-baseline gap-1 whitespace-nowrap rounded-full px-2 py-1.5 text-[12px] font-medium transition-colors duration-150';
const INK_TAB_ACTIVE = 'bg-ink-primary text-[var(--surface-2)]';
const INK_TAB_INACTIVE = 'text-ink-secondary hover:bg-[var(--surface-1)] hover:text-ink-primary';

/** Pill tab row for Interior surface toolbars. Active tab = filled neutral pill. */
export function SurfacePageTabs<T extends string>({
  items,
  value,
  onChange,
  'aria-label': ariaLabel,
  className,
  variant = 'soft',
  density = 'default',
}: SurfacePageTabsProps<T>) {
  const compact = variant === 'ink' && density === 'compact';
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn('flex flex-wrap items-center', compact ? 'gap-0.5' : 'gap-1', className)}
    >
      {items.map((item) => {
        const active = item.id === value;
        const tabClass = variant === 'ink'
          ? cn(compact ? INK_TAB_COMPACT : INK_TAB_BASE, active ? INK_TAB_ACTIVE : INK_TAB_INACTIVE)
          : cn(active ? SURFACE_PAGE_TAB_ACTIVE : SURFACE_PAGE_TAB_INACTIVE);
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className={tabClass}
          >
            {item.label}
            {item.count ? (
              <span
                className={cn(
                  'text-xs font-normal tabular-nums',
                  active && variant === 'ink'
                    ? 'text-[color-mix(in_srgb,var(--surface-2)_70%,transparent)]'
                    : 'text-muted',
                )}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
